import { useState, useEffect } from "react";

// The signed-in user's permissions, shared by every component that uses them.
//
// Layout fills this from the /auth/session/verify/ response it already waits for
// (setUserPermissions), so the navbar and pages have permissions the moment the
// app renders. Before that -- or if nothing has filled it -- the first component
// to ask fetches /auth/permissions/ once, and everyone waiting shares the result.
let cachedPermissions = null;
let inflightRequest = null;
const listeners = new Set();

function notify(value) {
  listeners.forEach((listener) => listener(value));
}

// Set (or with null, clear -- e.g. on logout) the shared permissions.
export function setUserPermissions(permissions) {
  cachedPermissions = permissions || null;
  if (cachedPermissions) notify({ permissions: cachedPermissions, error: null });
}

function fetchPermissionsOnce() {
  if (!inflightRequest) {
    inflightRequest = fetch("/api/auth/permissions/", { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Failed to fetch permissions");
        const data = await response.json();
        setUserPermissions(data);
        return data;
      })
      .catch((err) => {
        console.error("Permission fetch error:", err);
        notify({ permissions: cachedPermissions || {}, error: "Error fetching permissions" });
      })
      .finally(() => {
        inflightRequest = null;
      });
  }
  return inflightRequest;
}

export const useUserPermissions = () => {
  const [state, setState] = useState(() => ({
    permissions: cachedPermissions || {},
    loading: !cachedPermissions,
    error: null,
  }));

  useEffect(() => {
    const onChange = ({ permissions, error }) => {
      setState({ permissions: permissions || {}, loading: false, error });
    };
    listeners.add(onChange);
    if (cachedPermissions) {
      // Filled between the first render and this effect.
      onChange({ permissions: cachedPermissions, error: null });
    } else {
      fetchPermissionsOnce();
    }
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  return state;
};
